import { type NodePosition } from '../../hooks/commands/types';
import { computeSelectionBounds } from './nodeAlignment';
import { type NodeRect } from './nodeLayout';

// ── Constants ──────────────────────────────────────────────────────────

/**
 * Bucket size for rounding gaps when computing the mode.
 * Gaps within this tolerance are considered identical.
 */
const GAP_BUCKET_SIZE = 4;

/** Minimum gap for tidy operations (px). Matches NODE_GAP. */
const TIDY_MIN_GAP = 24;

/** Maximum gap for tidy operations (px). */
const TIDY_MAX_GAP = 64;

/** Fraction of average node dimension used for proportional gap fallback. */
const GAP_RATIO = 0.1;

// ── Types ──────────────────────────────────────────────────────────────

export type SelectionShape = 'horizontal' | 'vertical' | 'grid';

// ── Axis-generic grouping (center proximity) ─────────────────────────

/** Fraction of combined half-dimensions used as the group-split threshold. */
const GROUP_SPLIT_FACTOR = 0.8;

/**
 * Groups nodes along a given axis using center-coordinate proximity.
 *
 * Groups by the center coordinate along the chosen
 * axis, splitting into a new group when the distance from a node's center
 * to the **group's running average center** exceeds a size-aware threshold.
 *
 * The threshold is based on the node's dimension and the group's average
 * dimension, so a single tall/wide node cannot inflate the decision for
 * the entire group. This produces stable groupings even when nodes in
 * the same visual row/column have very different sizes.
 *
 * @param axis `'y'` groups into rows (sorted left-to-right within each row),
 *             `'x'` groups into columns (sorted top-to-bottom within each column).
 */
function groupNodesByAxis(nodes: NodeRect[], axis: 'x' | 'y'): NodeRect[][] {
  if (nodes.length === 0) {
    return [];
  }

  const isY = axis === 'y';
  const getCenter = (n: NodeRect) => (isY ? n.y + n.height / 2 : n.x + n.width / 2);
  const getDimension = (n: NodeRect) => (isY ? n.height : n.width);
  const crossSort = (a: NodeRect, b: NodeRect) => (isY ? a.x - b.x : a.y - b.y);

  const withMeta = nodes.map((n) => ({ center: getCenter(n), dimension: getDimension(n), node: n }));
  withMeta.sort((a, b) => a.center - b.center);

  const groups: NodeRect[][] = [[withMeta[0].node]];
  let groupAvgCenter = withMeta[0].center;
  let groupAvgDimension = withMeta[0].dimension;

  for (let i = 1; i < withMeta.length; i++) {
    const { center, dimension, node } = withMeta[i];
    const gap = center - groupAvgCenter;
    const threshold = (dimension + groupAvgDimension) * (GROUP_SPLIT_FACTOR / 2);

    if (gap > threshold) {
      groups.push([node]);
      groupAvgCenter = center;
      groupAvgDimension = dimension;
    } else {
      const currentGroup = groups[groups.length - 1];
      currentGroup.push(node);

      const count = currentGroup.length;
      groupAvgCenter = ((count - 1) * groupAvgCenter + center) / count;
      groupAvgDimension = currentGroup.reduce((sum, n) => sum + getDimension(n), 0) / count;
    }
  }

  for (const group of groups) {
    group.sort(crossSort);
  }

  return groups;
}

// ── Shape detection ────────────────────────────────────────────────────

/**
 * Determines the overall shape of a set of node rects by grouping
 * along both axes (Y → rows, X → columns) and picking the best fit.
 *
 * - Single row → `'horizontal'`
 * - Single column → `'vertical'`
 * - Every row has exactly 1 node → `'vertical'`
 * - Otherwise → `'grid'`
 */
export function detectSelectionShape(nodes: NodeRect[]): SelectionShape {
  if (nodes.length < 2) {
    return 'horizontal';
  }

  const rows = groupNodesByAxis(nodes, 'y');
  const cols = groupNodesByAxis(nodes, 'x');

  const isSingleRow = rows.length === 1;
  const isSingleCol = cols.length === 1;

  if (isSingleRow) {
    return 'horizontal';
  }

  if (isSingleCol) {
    return 'vertical';
  }

  if (rows.every((row) => row.length === 1)) {
    return 'vertical';
  }

  return 'grid';
}

// ── Gap calculation ────────────────────────────────────────────────────

/**
 * Finds the most common gap from a list of measured gaps.
 *
 * Gaps are bucketed to the nearest {@link GAP_BUCKET_SIZE} so that
 * near-identical spacings (e.g. 23px vs 25px) are treated as the same.
 * Returns the mode of the bucketed values, falling back to the median
 * when all gaps are unique. The result is clamped to at least
 * {@link TIDY_MIN_GAP}.
 */
function computeMostCommonGap(gaps: number[]): number {
  if (gaps.length === 0) {
    return TIDY_MIN_GAP;
  }

  const clamped = gaps.map((g) => Math.max(g, TIDY_MIN_GAP));

  if (clamped.length === 1) {
    return clamped[0];
  }

  const bucketed = clamped.map((g) => Math.round(g / GAP_BUCKET_SIZE) * GAP_BUCKET_SIZE);

  const freq = new Map<number, number>();
  for (const b of bucketed) {
    freq.set(b, (freq.get(b) ?? 0) + 1);
  }

  // Find mode (highest frequency, smallest value on tie)
  let modeValue = bucketed[0];
  let modeCount = 0;
  for (const [value, count] of freq) {
    if (count > modeCount || (count === modeCount && value < modeValue)) {
      modeValue = value;
      modeCount = count;
    }
  }

  // If all gaps are unique (mode count === 1), use median instead
  if (modeCount === 1) {
    const sorted = [...clamped].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];

    return Math.max(median, TIDY_MIN_GAP);
  }

  return Math.max(modeValue, TIDY_MIN_GAP);
}

// ── 1D Tidy ────────────────────────────────────────────────────────────

/**
 * Redistributes nodes along the horizontal axis with even spacing.
 *
 * 1. Sorts nodes left-to-right.
 * 2. Uses the most common horizontal gap as the spacing.
 * 3. Places nodes starting at the original left edge.
 * 4. Centers the **group as a whole** vertically within the original bounds
 *    (preserving each node's relative y offset from the group top).
 */
export function tidyHorizontalLine(nodes: NodeRect[]): NodePosition[] {
  if (nodes.length < 2) {
    return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: n.y } }));
  }

  const bounds = computeSelectionBounds(nodes);
  const sorted = [...nodes].sort((a, b) => a.x - b.x);

  const gaps: number[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    gaps.push(sorted[i + 1].x - (sorted[i].x + sorted[i].width));
  }
  const gap = computeMostCommonGap(gaps);

  const placed: Array<{ id: string; x: number; y: number; height: number }> = [];
  let currentX = bounds.minX;
  for (const node of sorted) {
    placed.push({ height: node.height, id: node.id, x: currentX, y: node.y });
    currentX += node.width + gap;
  }

  // Center the group as a whole vertically within the original bounds
  const groupMinY = Math.min(...placed.map((p) => p.y));
  const groupMaxY = Math.max(...placed.map((p) => p.y + p.height));
  const groupCenterY = (groupMinY + groupMaxY) / 2;
  const originalCenterY = bounds.minY + bounds.height / 2;
  const dy = originalCenterY - groupCenterY;

  return placed.map((p) => ({
    id: p.id,
    position: { x: p.x, y: p.y + dy },
  }));
}

/**
 * Redistributes nodes along the vertical axis with even spacing.
 *
 * 1. Sorts nodes top-to-bottom.
 * 2. Uses the most common vertical gap as the spacing.
 * 3. Places nodes starting at the original top edge.
 * 4. Centers the **group as a whole** horizontally within the original bounds
 *    (preserving each node's relative x offset from the group left).
 */
export function tidyVerticalLine(nodes: NodeRect[]): NodePosition[] {
  if (nodes.length < 2) {
    return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: n.y } }));
  }

  const bounds = computeSelectionBounds(nodes);
  const sorted = [...nodes].sort((a, b) => a.y - b.y);

  const gaps: number[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    gaps.push(sorted[i + 1].y - (sorted[i].y + sorted[i].height));
  }
  const gap = computeMostCommonGap(gaps);

  const placed: Array<{ id: string; x: number; y: number; width: number }> = [];
  let currentY = bounds.minY;
  for (const node of sorted) {
    placed.push({ id: node.id, width: node.width, x: node.x, y: currentY });
    currentY += node.height + gap;
  }

  // Center the group as a whole horizontally within the original bounds
  const groupMinX = Math.min(...placed.map((p) => p.x));
  const groupMaxX = Math.max(...placed.map((p) => p.x + p.width));
  const groupCenterX = (groupMinX + groupMaxX) / 2;
  const originalCenterX = bounds.minX + bounds.width / 2;
  const dx = originalCenterX - groupCenterX;

  return placed.map((p) => ({
    id: p.id,
    position: { x: p.x + dx, y: p.y },
  }));
}

// ── 2D Gap calculation ────────────────────────────────────────────────

/**
 * Computes visually pleasing column and row gaps for a grid layout.
 *
 * Uses a hybrid strategy: measure the existing gaps between nodes, then
 * take the larger of the existing gap and a proportional fallback
 * ({@link GAP_RATIO} × average node dimension). The result is clamped
 * to [{@link TIDY_MIN_GAP}, {@link TIDY_MAX_GAP}].
 */
function computeGridGaps(rows: NodeRect[][], zoom: number): { columnGap: number; rowGap: number } {
  const allNodes = rows.flat();
  const minGap = TIDY_MIN_GAP / zoom;
  const maxGap = TIDY_MAX_GAP / zoom;

  // ── Horizontal (column) gap ──
  const hGaps: number[] = [];
  for (const row of rows) {
    for (let i = 0; i < row.length - 1; i++) {
      hGaps.push(row[i + 1].x - (row[i].x + row[i].width));
    }
  }
  const existingH = computeMostCommonGap(hGaps);
  const avgWidth = allNodes.reduce((sum, n) => sum + n.width, 0) / allNodes.length;
  const proportionalH = avgWidth * GAP_RATIO;
  const columnGap = Math.min(Math.max(existingH, proportionalH, minGap), maxGap);

  // ── Vertical (row) gap ──
  const vGaps: number[] = [];
  for (let r = 0; r < rows.length - 1; r++) {
    const rowBottom = Math.max(...rows[r].map((n) => n.y + n.height));
    const nextRowTop = Math.min(...rows[r + 1].map((n) => n.y));
    vGaps.push(nextRowTop - rowBottom);
  }
  const existingV = computeMostCommonGap(vGaps);
  const avgHeight = allNodes.reduce((sum, n) => sum + n.height, 0) / allNodes.length;
  const proportionalV = avgHeight * GAP_RATIO;
  const rowGap = Math.min(Math.max(existingV, proportionalV, minGap), maxGap);

  return { columnGap, rowGap };
}

// ── 2D Tidy ────────────────────────────────────────────────────────────

/**
 * Redistributes nodes into an aligned grid, preserving the detected
 * row structure from {@link groupNodesByAxis}.
 *
 * - Column width = widest item in that column (across all rows).
 * - Row height = tallest item in that row.
 * - Starts from the top-left corner of the original selection bounds.
 */
export function tidyGrid(nodes: NodeRect[], zoom = 1): NodePosition[] {
  if (nodes.length < 2) {
    return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: n.y } }));
  }

  const bounds = computeSelectionBounds(nodes);
  const rows = groupNodesByAxis(nodes, 'y');
  const { columnGap, rowGap } = computeGridGaps(rows, zoom);

  const maxCols = Math.max(...rows.map((row) => row.length));
  const columnWidths: number[] = Array.from({ length: maxCols }, () => 0);
  for (const row of rows) {
    for (let col = 0; col < row.length; col++) {
      columnWidths[col] = Math.max(columnWidths[col], row[col].width);
    }
  }

  const rowHeights: number[] = rows.map((row) => Math.max(...row.map((n) => n.height)));

  const colX: number[] = [bounds.minX];
  for (let i = 1; i < maxCols; i++) {
    colX[i] = colX[i - 1] + columnWidths[i - 1] + columnGap;
  }

  const rowY: number[] = [bounds.minY];
  for (let j = 1; j < rows.length; j++) {
    rowY[j] = rowY[j - 1] + rowHeights[j - 1] + rowGap;
  }

  const positions: NodePosition[] = [];
  for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
    for (let colIdx = 0; colIdx < rows[rowIdx].length; colIdx++) {
      const node = rows[rowIdx][colIdx];
      positions.push({
        id: node.id,
        position: { x: colX[colIdx], y: rowY[rowIdx] },
      });
    }
  }

  return positions;
}

// ── Unified entry point ────────────────────────────────────────────────

/**
 * Tidies the given nodes based on the detected selection shape.
 *
 * - Horizontal line → evenly spaces along x, centers vertically.
 * - Vertical line → evenly spaces along y, centers horizontally.
 * - Grid → aligns into a clean grid with per-column widths and per-row heights.
 */
export function tidyNodes(nodes: NodeRect[], zoom = 1): NodePosition[] {
  if (nodes.length < 2) {
    return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: n.y } }));
  }

  const shape = detectSelectionShape(nodes);

  switch (shape) {
    case 'horizontal':
      return tidyHorizontalLine(nodes);
    case 'vertical':
      return tidyVerticalLine(nodes);
    case 'grid':
      return tidyGrid(nodes, zoom);
  }
}
